import { includes, isNil } from 'lodash';
import Image from 'next/image';
import { MouseEvent, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useToggle } from 'react-use';

import Typography from 'components/common/typography';
import useMedia from 'hooks/useMedia';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { cmaSelector } from 'store/slices/cma/selector';
import { cmaThunk } from 'store/slices/cma/thunks';
import { homeValuationSelector } from 'store/slices/homeValuation/selector';
import { Fields } from 'types/fields';
import messages from '../messages';
import ValuationResultStyled from '../valuation-result/styles';
import { media } from './constants';
import Styled from './styles';

export interface CMASectionProps {
  homeValuationForm: UseFormReturn<Fields>;
  cmaRequested: Record<string, string[]>;
  onCMARequest: (leadId: string, email: string) => void;
}

export default function CMASection({
  homeValuationForm,
  cmaRequested,
  onCMARequest,
}: CMASectionProps) {
  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = homeValuationForm;
  const dispatch = useAppDispatch();
  const { zohoHomeValuation } = useAppSelector(homeValuationSelector);
  const { cma } = useAppSelector(cmaSelector);
  const { isBelowSmallLaptop } = useMedia();
  const [isInputDisabled, toggleInput] = useToggle(true);
  const email = watch('email');

  const emailError = errors?.email?.message;
  const { id: leadId = '' } = zohoHomeValuation?.data || {};
  const isCMAReportSent = includes(cmaRequested?.[leadId], email);
  const isGetCMACTADisabled = !isInputDisabled || isCMAReportSent;

  function onEdit(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    toggleInput();
  }

  function onSubmit() {
    toggleInput();
  }

  function getCMAReport() {
    if (!isNil(leadId)) {
      dispatch(cmaThunk({ leadId, cma_email: email }));
    }
  }

  useEffect(() => {
    if (!isInputDisabled) {
      const element = document.querySelector(
        '.email-input'
      ) as HTMLInputElement;

      if (element) {
        element.focus();
      }
    }
  }, [isInputDisabled]);

  useEffect(() => {
    if (cma.data?.success && !isCMAReportSent) {
      onCMARequest(leadId, email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cma.data]);

  return (
    <Styled.Wrapper>
      <Styled.DotsWrapper>
        <Image
          src={media.dots}
          alt="desktop green dots"
          width={216}
          height={115}
          layout="fill"
        />
      </Styled.DotsWrapper>
      <Styled.ContentWrapper>
        <Typography as="h1" fontWeight="bold" variant="h1">
          <FormattedMessage {...messages.cmaHeading} />
        </Typography>
        <Typography as="p" fontWeight="semibold" variant="bodyMedium">
          <FormattedMessage {...messages.cmaDescription} />
        </Typography>
        <Styled.EmailInput>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Styled.InputWrapper>
              <Styled.Input
                className="email-input"
                type="email"
                disabled={isInputDisabled}
                {...register('email')}
              />
              <Styled.InputCTAWrapper>
                <Styled.InputDivider />
                {isInputDisabled ? (
                  <ValuationResultStyled.ButtonLink
                    type="button"
                    onClick={onEdit}
                  >
                    <FormattedMessage {...messages.editCTA} />
                  </ValuationResultStyled.ButtonLink>
                ) : (
                  <ValuationResultStyled.ButtonLink type="submit">
                    <Image src={media.tick} alt="tick" width={24} height={24} />
                  </ValuationResultStyled.ButtonLink>
                )}
              </Styled.InputCTAWrapper>
            </Styled.InputWrapper>
          </form>
          <Styled.ErrorMessage>{emailError}</Styled.ErrorMessage>
        </Styled.EmailInput>
        <ValuationResultStyled.ButtonContained
          className="button-contained"
          disabled={isGetCMACTADisabled}
          onClick={getCMAReport}
        >
          <FormattedMessage
            {...(isCMAReportSent ? messages.requestSent : messages.cmaCTA)}
          />
          {isCMAReportSent && (
            <Image
              className="tick-icon"
              src={media.tick}
              alt="tick"
              width={20}
              height={20}
            />
          )}
        </ValuationResultStyled.ButtonContained>
      </Styled.ContentWrapper>
      <Styled.ImageWrapper>
        <Image
          src={isBelowSmallLaptop ? media.cmaTableMobile : media.cmaTable}
          alt="cma table"
          width={594}
          height={412}
          layout="fill"
        />
      </Styled.ImageWrapper>
    </Styled.Wrapper>
  );
}
